import type { Language, LabTestId } from '../../types/document'

// Medical report copy. Machine-drafted outside Hindi and English: needs native-speaker
// (and ideally doctor) review. Descriptions say only what a test measures — never what
// a result means for the person's health.

export interface LabCopy {
  title: string
  high: string
  low: string
  normal: string
  noRange: string
  summaryOut: string
  summaryOk: string
  safety: string
  showDoctor: string
  range: string
  yourValue: string
  tests: Record<LabTestId, string>
}

export const LAB_COPY: Record<Language, LabCopy> = {
  en: {
    title: "Test results",
    high: "Above the range on your report",
    low: "Below the range on your report",
    normal: "Within the range on your report",
    noRange: "No range printed on the report",
    summaryOut: "{n} of {total} results are outside the range printed on your report.",
    summaryOk: "All {total} results are within the ranges printed on your report.",
    safety: "Samjho only compares each result with the range printed on your report. This is not a diagnosis. Please show the report to your doctor.",
    showDoctor: "Show these to your doctor",
    range: "Range",
    yourValue: "Your result",
    tests: {
      haemoglobin: "the part of blood that carries oxygen",
      wbc: "white blood cells, which fight infection",
      platelets: "cells that help blood clot",
      rbc: "red blood cells",
      fasting_sugar: "blood sugar after not eating overnight",
      pp_sugar: "blood sugar about 2 hours after a meal",
      random_sugar: "blood sugar at any time of day",
      hba1c: "average blood sugar over the last 3 months",
      cholesterol: "total cholesterol (a fat) in the blood",
      ldl: "\"bad\" cholesterol",
      hdl: "\"good\" cholesterol",
      triglycerides: "a type of fat in the blood",
      creatinine: "a waste product that shows how the kidneys are working",
      urea: "a waste product removed by the kidneys",
      uric_acid: "a waste product in the blood",
      sgpt: "a liver enzyme (ALT)",
      sgot: "a liver enzyme (AST)",
      bilirubin: "a yellow substance handled by the liver",
      tsh: "a hormone that shows how the thyroid is working",
      vitamin_d: "vitamin D level",
      vitamin_b12: "vitamin B12 level",
      esr: "a general sign of inflammation in the body",
    }
  },
  hi: {
    title: "जाँच के नतीजे",
    high: "रिपोर्ट में दी गई सीमा से ज़्यादा",
    low: "रिपोर्ट में दी गई सीमा से कम",
    normal: "रिपोर्ट में दी गई सीमा के अंदर",
    noRange: "रिपोर्ट में सीमा नहीं दी गई",
    summaryOut: "{total} में से {n} नतीजे रिपोर्ट में दी गई सीमा से बाहर हैं।",
    summaryOk: "सभी {total} नतीजे रिपोर्ट में दी गई सीमा के अंदर हैं।",
    safety: "समझो हर नतीजे को सिर्फ़ रिपोर्ट में छपी सीमा से मिलाता है। यह बीमारी की जाँच (डायग्नोसिस) नहीं है। रिपोर्ट अपने डॉक्टर को ज़रूर दिखाएँ।",
    showDoctor: "ये डॉक्टर को दिखाएँ",
    range: "सीमा",
    yourValue: "आपका नतीजा",
    tests: {
      haemoglobin: "खून का वह हिस्सा जो ऑक्सीजन ले जाता है",
      wbc: "सफ़ेद रक्त कोशिकाएँ, जो संक्रमण से लड़ती हैं",
      platelets: "कोशिकाएँ जो खून जमने में मदद करती हैं",
      rbc: "लाल रक्त कोशिकाएँ",
      fasting_sugar: "रात भर खाली पेट रहने के बाद खून में शुगर",
      pp_sugar: "खाने के लगभग 2 घंटे बाद खून में शुगर",
      random_sugar: "दिन में किसी भी समय खून में शुगर",
      hba1c: "पिछले 3 महीनों की औसत शुगर",
      cholesterol: "खून में कुल कोलेस्ट्रॉल (एक तरह की चर्बी)",
      ldl: "\"ख़राब\" कोलेस्ट्रॉल",
      hdl: "\"अच्छा\" कोलेस्ट्रॉल",
      triglycerides: "खून में एक तरह की चर्बी",
      creatinine: "एक अपशिष्ट जो बताता है कि गुर्दे कैसे काम कर रहे हैं",
      urea: "एक अपशिष्ट जिसे गुर्दे बाहर निकालते हैं",
      uric_acid: "खून में एक अपशिष्ट पदार्थ",
      sgpt: "लिवर का एक एंज़ाइम (ALT)",
      sgot: "लिवर का एक एंज़ाइम (AST)",
      bilirubin: "एक पीला पदार्थ जिसे लिवर संभालता है",
      tsh: "एक हार्मोन जो बताता है कि थायरॉइड कैसे काम कर रहा है",
      vitamin_d: "विटामिन D का स्तर",
      vitamin_b12: "विटामिन B12 का स्तर",
      esr: "शरीर में सूजन का एक सामान्य संकेत",
    }
  },
  bn: {
    title: "পরীক্ষার ফলাফল",
    high: "রিপোর্টে দেওয়া সীমার চেয়ে বেশি",
    low: "রিপোর্টে দেওয়া সীমার চেয়ে কম",
    normal: "রিপোর্টে দেওয়া সীমার মধ্যে",
    noRange: "রিপোর্টে সীমা দেওয়া নেই",
    summaryOut: "{total}টির মধ্যে {n}টি ফলাফল রিপোর্টে দেওয়া সীমার বাইরে।",
    summaryOk: "সব {total}টি ফলাফল রিপোর্টে দেওয়া সীমার মধ্যে।",
    safety: "সমঝো প্রতিটি ফলাফল শুধু রিপোর্টে ছাপা সীমার সঙ্গে মেলায়। এটি রোগনির্ণয় নয়। রিপোর্টটি অবশ্যই আপনার ডাক্তারকে দেখান।",
    showDoctor: "এগুলো ডাক্তারকে দেখান",
    range: "সীমা",
    yourValue: "আপনার ফলাফল",
    tests: {
      haemoglobin: "রক্তের যে অংশ অক্সিজেন বহন করে",
      wbc: "শ্বেত রক্তকণিকা, যা সংক্রমণের সঙ্গে লড়ে",
      platelets: "যে কোষ রক্ত জমাট বাঁধতে সাহায্য করে",
      rbc: "লোহিত রক্তকণিকা",
      fasting_sugar: "সারা রাত না খেয়ে থাকার পর রক্তে শর্করা",
      pp_sugar: "খাওয়ার প্রায় ২ ঘণ্টা পর রক্তে শর্করা",
      random_sugar: "দিনের যেকোনো সময় রক্তে শর্করা",
      hba1c: "গত ৩ মাসের গড় রক্তে শর্করা",
      cholesterol: "রক্তে মোট কোলেস্টেরল (এক ধরনের চর্বি)",
      ldl: "\"খারাপ\" কোলেস্টেরল",
      hdl: "\"ভালো\" কোলেস্টেরল",
      triglycerides: "রক্তে এক ধরনের চর্বি",
      creatinine: "একটি বর্জ্য পদার্থ যা দেখায় কিডনি কেমন কাজ করছে",
      urea: "একটি বর্জ্য পদার্থ যা কিডনি বের করে দেয়",
      uric_acid: "রক্তে একটি বর্জ্য পদার্থ",
      sgpt: "লিভারের একটি এনজাইম (ALT)",
      sgot: "লিভারের একটি এনজাইম (AST)",
      bilirubin: "লিভার যে হলুদ পদার্থ সামলায়",
      tsh: "একটি হরমোন যা দেখায় থাইরয়েড কেমন কাজ করছে",
      vitamin_d: "ভিটামিন D-এর মাত্রা",
      vitamin_b12: "ভিটামিন B12-এর মাত্রা",
      esr: "শরীরে প্রদাহের একটি সাধারণ লক্ষণ",
    }
  },
  mr: {
    title: "तपासणीचे निकाल",
    high: "अहवालातील मर्यादेपेक्षा जास्त",
    low: "अहवालातील मर्यादेपेक्षा कमी",
    normal: "अहवालातील मर्यादेच्या आत",
    noRange: "अहवालात मर्यादा दिलेली नाही",
    summaryOut: "{total} पैकी {n} निकाल अहवालात दिलेल्या मर्यादेबाहेर आहेत.",
    summaryOk: "सर्व {total} निकाल अहवालात दिलेल्या मर्यादेच्या आत आहेत.",
    safety: "समझो प्रत्येक निकालाची तुलना फक्त अहवालात छापलेल्या मर्यादेशी करते. हे रोगनिदान नाही. अहवाल तुमच्या डॉक्टरांना नक्की दाखवा.",
    showDoctor: "हे डॉक्टरांना दाखवा",
    range: "मर्यादा",
    yourValue: "तुमचा निकाल",
    tests: {
      haemoglobin: "रक्तातील ऑक्सिजन वाहून नेणारा भाग",
      wbc: "पांढऱ्या रक्तपेशी, ज्या संसर्गाशी लढतात",
      platelets: "रक्त गोठण्यास मदत करणाऱ्या पेशी",
      rbc: "लाल रक्तपेशी",
      fasting_sugar: "रात्रभर उपाशी राहिल्यानंतर रक्तातील साखर",
      pp_sugar: "जेवणानंतर सुमारे 2 तासांनी रक्तातील साखर",
      random_sugar: "दिवसाच्या कोणत्याही वेळी रक्तातील साखर",
      hba1c: "मागील 3 महिन्यांची सरासरी साखर",
      cholesterol: "रक्तातील एकूण कोलेस्टेरॉल (एक प्रकारची चरबी)",
      ldl: "\"वाईट\" कोलेस्टेरॉल",
      hdl: "\"चांगले\" कोलेस्टेरॉल",
      triglycerides: "रक्तातील एक प्रकारची चरबी",
      creatinine: "मूत्रपिंड कसे काम करत आहेत हे दाखवणारा टाकाऊ पदार्थ",
      urea: "मूत्रपिंड बाहेर टाकतात तो टाकाऊ पदार्थ",
      uric_acid: "रक्तातील एक टाकाऊ पदार्थ",
      sgpt: "यकृताचे एक एन्झाइम (ALT)",
      sgot: "यकृताचे एक एन्झाइम (AST)",
      bilirubin: "यकृत हाताळते तो पिवळा पदार्थ",
      tsh: "थायरॉइड कसे काम करत आहे हे दाखवणारे हार्मोन",
      vitamin_d: "व्हिटॅमिन D चे प्रमाण",
      vitamin_b12: "व्हिटॅमिन B12 चे प्रमाण",
      esr: "शरीरातील सुजेचे एक सर्वसाधारण लक्षण",
    }
  },
  ta: {
    title: "பரிசோதனை முடிவுகள்",
    high: "அறிக்கையில் உள்ள அளவை விட அதிகம்",
    low: "அறிக்கையில் உள்ள அளவை விடக் குறைவு",
    normal: "அறிக்கையில் உள்ள அளவுக்குள்",
    noRange: "அறிக்கையில் அளவு குறிப்பிடப்படவில்லை",
    summaryOut: "{total} முடிவுகளில் {n} அறிக்கையில் உள்ள அளவுக்கு வெளியே உள்ளன.",
    summaryOk: "அனைத்து {total} முடிவுகளும் அறிக்கையில் உள்ள அளவுக்குள் உள்ளன.",
    safety: "சம்ஜோ ஒவ்வொரு முடிவையும் அறிக்கையில் அச்சிடப்பட்ட அளவுடன் மட்டுமே ஒப்பிடுகிறது. இது நோய் கண்டறிதல் அல்ல. அறிக்கையை உங்கள் மருத்துவரிடம் காட்டுங்கள்.",
    showDoctor: "இவற்றை மருத்துவரிடம் காட்டுங்கள்",
    range: "அளவு",
    yourValue: "உங்கள் முடிவு",
    tests: {
      haemoglobin: "ஆக்ஸிஜனைக் கொண்டு செல்லும் இரத்தத்தின் பகுதி",
      wbc: "தொற்றுக்கு எதிராகப் போராடும் வெள்ளை இரத்த அணுக்கள்",
      platelets: "இரத்தம் உறைய உதவும் அணுக்கள்",
      rbc: "சிவப்பு இரத்த அணுக்கள்",
      fasting_sugar: "இரவு முழுவதும் சாப்பிடாத பிறகு இரத்தச் சர்க்கரை",
      pp_sugar: "சாப்பிட்டு சுமார் 2 மணி நேரம் கழித்து இரத்தச் சர்க்கரை",
      random_sugar: "நாளின் எந்த நேரத்திலும் இரத்தச் சர்க்கரை",
      hba1c: "கடந்த 3 மாதங்களின் சராசரி இரத்தச் சர்க்கரை",
      cholesterol: "இரத்தத்தில் உள்ள மொத்த கொலஸ்ட்ரால் (ஒரு வகை கொழுப்பு)",
      ldl: "\"கெட்ட\" கொலஸ்ட்ரால்",
      hdl: "\"நல்ல\" கொலஸ்ட்ரால்",
      triglycerides: "இரத்தத்தில் உள்ள ஒரு வகை கொழுப்பு",
      creatinine: "சிறுநீரகங்கள் எப்படி வேலை செய்கின்றன என்பதைக் காட்டும் கழிவுப் பொருள்",
      urea: "சிறுநீரகங்கள் வெளியேற்றும் கழிவுப் பொருள்",
      uric_acid: "இரத்தத்தில் உள்ள ஒரு கழிவுப் பொருள்",
      sgpt: "கல்லீரலின் ஒரு நொதி (ALT)",
      sgot: "கல்லீரலின் ஒரு நொதி (AST)",
      bilirubin: "கல்லீரல் கையாளும் மஞ்சள் பொருள்",
      tsh: "தைராய்டு எப்படி வேலை செய்கிறது என்பதைக் காட்டும் ஹார்மோன்",
      vitamin_d: "வைட்டமின் D அளவு",
      vitamin_b12: "வைட்டமின் B12 அளவு",
      esr: "உடலில் வீக்கத்தின் பொதுவான அறிகுறி",
    }
  },
  te: {
    title: "పరీక్ష ఫలితాలు",
    high: "నివేదికలోని పరిమితి కంటే ఎక్కువ",
    low: "నివేదికలోని పరిమితి కంటే తక్కువ",
    normal: "నివేదికలోని పరిమితి లోపల",
    noRange: "నివేదికలో పరిమితి ఇవ్వలేదు",
    summaryOut: "{total} ఫలితాల్లో {n} నివేదికలోని పరిమితికి బయట ఉన్నాయి.",
    summaryOk: "మొత్తం {total} ఫలితాలు నివేదికలోని పరిమితి లోపలే ఉన్నాయి.",
    safety: "సమ్ఝో ప్రతి ఫలితాన్ని నివేదికలో ముద్రించిన పరిమితితో మాత్రమే పోలుస్తుంది. ఇది వ్యాధి నిర్ధారణ కాదు. నివేదికను తప్పకుండా మీ డాక్టర్‌కు చూపించండి.",
    showDoctor: "వీటిని డాక్టర్‌కు చూపించండి",
    range: "పరిమితి",
    yourValue: "మీ ఫలితం",
    tests: {
      haemoglobin: "ఆక్సిజన్‌ను తీసుకెళ్లే రక్త భాగం",
      wbc: "ఇన్ఫెక్షన్‌తో పోరాడే తెల్ల రక్త కణాలు",
      platelets: "రక్తం గడ్డకట్టడానికి సహాయపడే కణాలు",
      rbc: "ఎర్ర రక్త కణాలు",
      fasting_sugar: "రాత్రంతా ఏమీ తినకుండా ఉన్న తర్వాత రక్తంలో చక్కెర",
      pp_sugar: "భోజనం తర్వాత సుమారు 2 గంటలకు రక్తంలో చక్కెర",
      random_sugar: "రోజులో ఏ సమయంలోనైనా రక్తంలో చక్కెర",
      hba1c: "గత 3 నెలల సగటు రక్తంలో చక్కెర",
      cholesterol: "రక్తంలో మొత్తం కొలెస్ట్రాల్ (ఒక రకమైన కొవ్వు)",
      ldl: "\"చెడు\" కొలెస్ట్రాల్",
      hdl: "\"మంచి\" కొలెస్ట్రాల్",
      triglycerides: "రక్తంలో ఒక రకమైన కొవ్వు",
      creatinine: "మూత్రపిండాలు ఎలా పనిచేస్తున్నాయో చూపే వ్యర్థ పదార్థం",
      urea: "మూత్రపిండాలు బయటకు పంపే వ్యర్థ పదార్థం",
      uric_acid: "రక్తంలోని ఒక వ్యర్థ పదార్థం",
      sgpt: "కాలేయంలోని ఒక ఎంజైమ్ (ALT)",
      sgot: "కాలేయంలోని ఒక ఎంజైమ్ (AST)",
      bilirubin: "కాలేయం నిర్వహించే పసుపు పదార్థం",
      tsh: "థైరాయిడ్ ఎలా పనిచేస్తుందో చూపే హార్మోన్",
      vitamin_d: "విటమిన్ D స్థాయి",
      vitamin_b12: "విటమిన్ B12 స్థాయి",
      esr: "శరీరంలో వాపు యొక్క సాధారణ సూచన",
    }
  },
  gu: {
    title: "તપાસનાં પરિણામો",
    high: "રિપોર્ટમાં આપેલી મર્યાદા કરતાં વધુ",
    low: "રિપોર્ટમાં આપેલી મર્યાદા કરતાં ઓછું",
    normal: "રિપોર્ટમાં આપેલી મર્યાદાની અંદર",
    noRange: "રિપોર્ટમાં મર્યાદા આપેલી નથી",
    summaryOut: "{total}માંથી {n} પરિણામો રિપોર્ટમાં આપેલી મર્યાદાની બહાર છે.",
    summaryOk: "બધાં {total} પરિણામો રિપોર્ટમાં આપેલી મર્યાદાની અંદર છે.",
    safety: "સમજો દરેક પરિણામને ફક્ત રિપોર્ટમાં છાપેલી મર્યાદા સાથે સરખાવે છે. આ રોગનું નિદાન નથી. રિપોર્ટ તમારા ડૉક્ટરને જરૂર બતાવો.",
    showDoctor: "આ ડૉક્ટરને બતાવો",
    range: "મર્યાદા",
    yourValue: "તમારું પરિણામ",
    tests: {
      haemoglobin: "લોહીનો ઓક્સિજન લઈ જતો ભાગ",
      wbc: "સફેદ રક્તકણો, જે ચેપ સામે લડે છે",
      platelets: "લોહી ગંઠાવામાં મદદ કરતા કોષો",
      rbc: "લાલ રક્તકણો",
      fasting_sugar: "આખી રાત ભૂખ્યા રહ્યા પછી લોહીમાં ખાંડ",
      pp_sugar: "જમ્યાના આશરે 2 કલાક પછી લોહીમાં ખાંડ",
      random_sugar: "દિવસના કોઈપણ સમયે લોહીમાં ખાંડ",
      hba1c: "છેલ્લા 3 મહિનાની સરેરાશ ખાંડ",
      cholesterol: "લોહીમાં કુલ કોલેસ્ટ્રોલ (એક પ્રકારની ચરબી)",
      ldl: "\"ખરાબ\" કોલેસ્ટ્રોલ",
      hdl: "\"સારું\" કોલેસ્ટ્રોલ",
      triglycerides: "લોહીમાં એક પ્રકારની ચરબી",
      creatinine: "કિડની કેવી રીતે કામ કરે છે તે બતાવતો કચરો",
      urea: "કિડની બહાર કાઢે છે તે કચરો",
      uric_acid: "લોહીમાં એક કચરો",
      sgpt: "લિવરનું એક એન્ઝાઇમ (ALT)",
      sgot: "લિવરનું એક એન્ઝાઇમ (AST)",
      bilirubin: "લિવર સંભાળે છે તે પીળો પદાર્થ",
      tsh: "થાઇરોઇડ કેવી રીતે કામ કરે છે તે બતાવતો હોર્મોન",
      vitamin_d: "વિટામિન D નું સ્તર",
      vitamin_b12: "વિટામિન B12 નું સ્તર",
      esr: "શરીરમાં સોજાની એક સામાન્ય નિશાની",
    }
  },
  kn: {
    title: "ಪರೀಕ್ಷಾ ಫಲಿತಾಂಶಗಳು",
    high: "ವರದಿಯಲ್ಲಿರುವ ಮಿತಿಗಿಂತ ಹೆಚ್ಚು",
    low: "ವರದಿಯಲ್ಲಿರುವ ಮಿತಿಗಿಂತ ಕಡಿಮೆ",
    normal: "ವರದಿಯಲ್ಲಿರುವ ಮಿತಿಯೊಳಗೆ",
    noRange: "ವರದಿಯಲ್ಲಿ ಮಿತಿ ನೀಡಿಲ್ಲ",
    summaryOut: "{total} ಫಲಿತಾಂಶಗಳಲ್ಲಿ {n} ವರದಿಯಲ್ಲಿರುವ ಮಿತಿಯ ಹೊರಗಿವೆ.",
    summaryOk: "ಎಲ್ಲಾ {total} ಫಲಿತಾಂಶಗಳು ವರದಿಯಲ್ಲಿರುವ ಮಿತಿಯೊಳಗಿವೆ.",
    safety: "ಸಮ್ಝೋ ಪ್ರತಿ ಫಲಿತಾಂಶವನ್ನು ವರದಿಯಲ್ಲಿ ಮುದ್ರಿಸಿರುವ ಮಿತಿಯೊಂದಿಗೆ ಮಾತ್ರ ಹೋಲಿಸುತ್ತದೆ. ಇದು ರೋಗನಿರ್ಣಯವಲ್ಲ. ವರದಿಯನ್ನು ನಿಮ್ಮ ವೈದ್ಯರಿಗೆ ತೋರಿಸಿ.",
    showDoctor: "ಇವನ್ನು ವೈದ್ಯರಿಗೆ ತೋರಿಸಿ",
    range: "ಮಿತಿ",
    yourValue: "ನಿಮ್ಮ ಫಲಿತಾಂಶ",
    tests: {
      haemoglobin: "ಆಮ್ಲಜನಕವನ್ನು ಸಾಗಿಸುವ ರಕ್ತದ ಭಾಗ",
      wbc: "ಸೋಂಕಿನ ವಿರುದ್ಧ ಹೋರಾಡುವ ಬಿಳಿ ರಕ್ತಕಣಗಳು",
      platelets: "ರಕ್ತ ಹೆಪ್ಪುಗಟ್ಟಲು ಸಹಾಯ ಮಾಡುವ ಕಣಗಳು",
      rbc: "ಕೆಂಪು ರಕ್ತಕಣಗಳು",
      fasting_sugar: "ರಾತ್ರಿಯಿಡೀ ಏನೂ ತಿನ್ನದೆ ಇದ್ದ ನಂತರ ರಕ್ತದಲ್ಲಿ ಸಕ್ಕರೆ",
      pp_sugar: "ಊಟದ ಸುಮಾರು 2 ಗಂಟೆಗಳ ನಂತರ ರಕ್ತದಲ್ಲಿ ಸಕ್ಕರೆ",
      random_sugar: "ದಿನದ ಯಾವುದೇ ಸಮಯದಲ್ಲಿ ರಕ್ತದಲ್ಲಿ ಸಕ್ಕರೆ",
      hba1c: "ಕಳೆದ 3 ತಿಂಗಳ ಸರಾಸರಿ ಸಕ್ಕರೆ",
      cholesterol: "ರಕ್ತದಲ್ಲಿನ ಒಟ್ಟು ಕೊಲೆಸ್ಟ್ರಾಲ್ (ಒಂದು ರೀತಿಯ ಕೊಬ್ಬು)",
      ldl: "\"ಕೆಟ್ಟ\" ಕೊಲೆಸ್ಟ್ರಾಲ್",
      hdl: "\"ಒಳ್ಳೆಯ\" ಕೊಲೆಸ್ಟ್ರಾಲ್",
      triglycerides: "ರಕ್ತದಲ್ಲಿನ ಒಂದು ರೀತಿಯ ಕೊಬ್ಬು",
      creatinine: "ಮೂತ್ರಪಿಂಡಗಳು ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತಿವೆ ಎಂದು ತೋರಿಸುವ ತ್ಯಾಜ್ಯ",
      urea: "ಮೂತ್ರಪಿಂಡಗಳು ಹೊರಹಾಕುವ ತ್ಯಾಜ್ಯ",
      uric_acid: "ರಕ್ತದಲ್ಲಿನ ಒಂದು ತ್ಯಾಜ್ಯ",
      sgpt: "ಯಕೃತ್ತಿನ ಒಂದು ಕಿಣ್ವ (ALT)",
      sgot: "ಯಕೃತ್ತಿನ ಒಂದು ಕಿಣ್ವ (AST)",
      bilirubin: "ಯಕೃತ್ತು ನಿರ್ವಹಿಸುವ ಹಳದಿ ವಸ್ತು",
      tsh: "ಥೈರಾಯ್ಡ್ ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತಿದೆ ಎಂದು ತೋರಿಸುವ ಹಾರ್ಮೋನ್",
      vitamin_d: "ವಿಟಮಿನ್ D ಮಟ್ಟ",
      vitamin_b12: "ವಿಟಮಿನ್ B12 ಮಟ್ಟ",
      esr: "ದೇಹದಲ್ಲಿ ಉರಿಯೂತದ ಸಾಮಾನ್ಯ ಸೂಚನೆ",
    }
  },
  ml: {
    title: "പരിശോധനാ ഫലങ്ങൾ",
    high: "റിപ്പോർട്ടിലെ പരിധിയേക്കാൾ കൂടുതൽ",
    low: "റിപ്പോർട്ടിലെ പരിധിയേക്കാൾ കുറവ്",
    normal: "റിപ്പോർട്ടിലെ പരിധിക്കുള്ളിൽ",
    noRange: "റിപ്പോർട്ടിൽ പരിധി നൽകിയിട്ടില്ല",
    summaryOut: "{total} ഫലങ്ങളിൽ {n} എണ്ണം റിപ്പോർട്ടിലെ പരിധിക്ക് പുറത്താണ്.",
    summaryOk: "എല്ലാ {total} ഫലങ്ങളും റിപ്പോർട്ടിലെ പരിധിക്കുള്ളിലാണ്.",
    safety: "സമ്ഝോ ഓരോ ഫലവും റിപ്പോർട്ടിൽ അച്ചടിച്ച പരിധിയുമായി മാത്രമേ താരതമ്യം ചെയ്യൂ. ഇത് രോഗനിർണയമല്ല. റിപ്പോർട്ട് നിങ്ങളുടെ ഡോക്ടറെ കാണിക്കുക.",
    showDoctor: "ഇവ ഡോക്ടറെ കാണിക്കുക",
    range: "പരിധി",
    yourValue: "നിങ്ങളുടെ ഫലം",
    tests: {
      haemoglobin: "ഓക്സിജൻ കൊണ്ടുപോകുന്ന രക്തത്തിന്റെ ഭാഗം",
      wbc: "അണുബാധയോട് പോരാടുന്ന വെളുത്ത രക്താണുക്കൾ",
      platelets: "രക്തം കട്ടപിടിക്കാൻ സഹായിക്കുന്ന കോശങ്ങൾ",
      rbc: "ചുവന്ന രക്താണുക്കൾ",
      fasting_sugar: "രാത്രി മുഴുവൻ ഭക്ഷണം കഴിക്കാതിരുന്ന ശേഷം രക്തത്തിലെ പഞ്ചസാര",
      pp_sugar: "ഭക്ഷണം കഴിഞ്ഞ് ഏകദേശം 2 മണിക്കൂറിന് ശേഷം രക്തത്തിലെ പഞ്ചസാര",
      random_sugar: "ദിവസത്തിലെ ഏത് സമയത്തും രക്തത്തിലെ പഞ്ചസാര",
      hba1c: "കഴിഞ്ഞ 3 മാസത്തെ ശരാശരി പഞ്ചസാര",
      cholesterol: "രക്തത്തിലെ ആകെ കൊളസ്ട്രോൾ (ഒരുതരം കൊഴുപ്പ്)",
      ldl: "\"മോശം\" കൊളസ്ട്രോൾ",
      hdl: "\"നല്ല\" കൊളസ്ട്രോൾ",
      triglycerides: "രക്തത്തിലെ ഒരുതരം കൊഴുപ്പ്",
      creatinine: "വൃക്കകൾ എങ്ങനെ പ്രവർത്തിക്കുന്നു എന്ന് കാണിക്കുന്ന മാലിന്യം",
      urea: "വൃക്കകൾ പുറന്തള്ളുന്ന മാലിന്യം",
      uric_acid: "രക്തത്തിലെ ഒരു മാലിന്യ വസ്തു",
      sgpt: "കരളിലെ ഒരു എൻസൈം (ALT)",
      sgot: "കരളിലെ ഒരു എൻസൈം (AST)",
      bilirubin: "കരൾ കൈകാര്യം ചെയ്യുന്ന മഞ്ഞ വസ്തു",
      tsh: "തൈറോയ്ഡ് എങ്ങനെ പ്രവർത്തിക്കുന്നു എന്ന് കാണിക്കുന്ന ഹോർമോൺ",
      vitamin_d: "വിറ്റാമിൻ D നില",
      vitamin_b12: "വിറ്റാമിൻ B12 നില",
      esr: "ശരീരത്തിലെ വീക്കത്തിന്റെ പൊതുവായ സൂചന",
    }
  },
  pa: {
    title: "ਜਾਂਚ ਦੇ ਨਤੀਜੇ",
    high: "ਰਿਪੋਰਟ ਵਿੱਚ ਦਿੱਤੀ ਸੀਮਾ ਤੋਂ ਵੱਧ",
    low: "ਰਿਪੋਰਟ ਵਿੱਚ ਦਿੱਤੀ ਸੀਮਾ ਤੋਂ ਘੱਟ",
    normal: "ਰਿਪੋਰਟ ਵਿੱਚ ਦਿੱਤੀ ਸੀਮਾ ਦੇ ਅੰਦਰ",
    noRange: "ਰਿਪੋਰਟ ਵਿੱਚ ਸੀਮਾ ਨਹੀਂ ਦਿੱਤੀ",
    summaryOut: "{total} ਵਿੱਚੋਂ {n} ਨਤੀਜੇ ਰਿਪੋਰਟ ਵਿੱਚ ਦਿੱਤੀ ਸੀਮਾ ਤੋਂ ਬਾਹਰ ਹਨ।",
    summaryOk: "ਸਾਰੇ {total} ਨਤੀਜੇ ਰਿਪੋਰਟ ਵਿੱਚ ਦਿੱਤੀ ਸੀਮਾ ਦੇ ਅੰਦਰ ਹਨ।",
    safety: "ਸਮਝੋ ਹਰ ਨਤੀਜੇ ਨੂੰ ਸਿਰਫ਼ ਰਿਪੋਰਟ ਵਿੱਚ ਛਪੀ ਸੀਮਾ ਨਾਲ ਮਿਲਾਉਂਦਾ ਹੈ। ਇਹ ਬਿਮਾਰੀ ਦੀ ਪਛਾਣ ਨਹੀਂ ਹੈ। ਰਿਪੋਰਟ ਆਪਣੇ ਡਾਕਟਰ ਨੂੰ ਜ਼ਰੂਰ ਦਿਖਾਓ।",
    showDoctor: "ਇਹ ਡਾਕਟਰ ਨੂੰ ਦਿਖਾਓ",
    range: "ਸੀਮਾ",
    yourValue: "ਤੁਹਾਡਾ ਨਤੀਜਾ",
    tests: {
      haemoglobin: "ਖ਼ੂਨ ਦਾ ਉਹ ਹਿੱਸਾ ਜੋ ਆਕਸੀਜਨ ਲੈ ਕੇ ਜਾਂਦਾ ਹੈ",
      wbc: "ਚਿੱਟੇ ਖ਼ੂਨ ਸੈੱਲ, ਜੋ ਲਾਗ ਨਾਲ ਲੜਦੇ ਹਨ",
      platelets: "ਸੈੱਲ ਜੋ ਖ਼ੂਨ ਜੰਮਣ ਵਿੱਚ ਮਦਦ ਕਰਦੇ ਹਨ",
      rbc: "ਲਾਲ ਖ਼ੂਨ ਸੈੱਲ",
      fasting_sugar: "ਸਾਰੀ ਰਾਤ ਖ਼ਾਲੀ ਪੇਟ ਰਹਿਣ ਤੋਂ ਬਾਅਦ ਖ਼ੂਨ ਵਿੱਚ ਸ਼ੂਗਰ",
      pp_sugar: "ਖਾਣੇ ਤੋਂ ਲਗਭਗ 2 ਘੰਟੇ ਬਾਅਦ ਖ਼ੂਨ ਵਿੱਚ ਸ਼ੂਗਰ",
      random_sugar: "ਦਿਨ ਵਿੱਚ ਕਿਸੇ ਵੀ ਸਮੇਂ ਖ਼ੂਨ ਵਿੱਚ ਸ਼ੂਗਰ",
      hba1c: "ਪਿਛਲੇ 3 ਮਹੀਨਿਆਂ ਦੀ ਔਸਤ ਸ਼ੂਗਰ",
      cholesterol: "ਖ਼ੂਨ ਵਿੱਚ ਕੁੱਲ ਕੋਲੈਸਟ੍ਰੋਲ (ਇੱਕ ਕਿਸਮ ਦੀ ਚਰਬੀ)",
      ldl: "\"ਮਾੜਾ\" ਕੋਲੈਸਟ੍ਰੋਲ",
      hdl: "\"ਚੰਗਾ\" ਕੋਲੈਸਟ੍ਰੋਲ",
      triglycerides: "ਖ਼ੂਨ ਵਿੱਚ ਇੱਕ ਕਿਸਮ ਦੀ ਚਰਬੀ",
      creatinine: "ਇੱਕ ਫ਼ਾਲਤੂ ਪਦਾਰਥ ਜੋ ਦੱਸਦਾ ਹੈ ਕਿ ਗੁਰਦੇ ਕਿਵੇਂ ਕੰਮ ਕਰ ਰਹੇ ਹਨ",
      urea: "ਇੱਕ ਫ਼ਾਲਤੂ ਪਦਾਰਥ ਜਿਸ ਨੂੰ ਗੁਰਦੇ ਬਾਹਰ ਕੱਢਦੇ ਹਨ",
      uric_acid: "ਖ਼ੂਨ ਵਿੱਚ ਇੱਕ ਫ਼ਾਲਤੂ ਪਦਾਰਥ",
      sgpt: "ਜਿਗਰ ਦਾ ਇੱਕ ਐਂਜ਼ਾਈਮ (ALT)",
      sgot: "ਜਿਗਰ ਦਾ ਇੱਕ ਐਂਜ਼ਾਈਮ (AST)",
      bilirubin: "ਇੱਕ ਪੀਲਾ ਪਦਾਰਥ ਜਿਸ ਨੂੰ ਜਿਗਰ ਸੰਭਾਲਦਾ ਹੈ",
      tsh: "ਇੱਕ ਹਾਰਮੋਨ ਜੋ ਦੱਸਦਾ ਹੈ ਕਿ ਥਾਇਰਾਇਡ ਕਿਵੇਂ ਕੰਮ ਕਰ ਰਿਹਾ ਹੈ",
      vitamin_d: "ਵਿਟਾਮਿਨ D ਦਾ ਪੱਧਰ",
      vitamin_b12: "ਵਿਟਾਮਿਨ B12 ਦਾ ਪੱਧਰ",
      esr: "ਸਰੀਰ ਵਿੱਚ ਸੋਜ ਦਾ ਇੱਕ ਆਮ ਸੰਕੇਤ",
    }
  },
  or: {
    title: "ପରୀକ୍ଷା ଫଳାଫଳ",
    high: "ରିପୋର୍ଟରେ ଦିଆଯାଇଥିବା ସୀମାଠାରୁ ଅଧିକ",
    low: "ରିପୋର୍ଟରେ ଦିଆଯାଇଥିବା ସୀମାଠାରୁ କମ୍",
    normal: "ରିପୋର୍ଟରେ ଦିଆଯାଇଥିବା ସୀମା ମଧ୍ୟରେ",
    noRange: "ରିପୋର୍ଟରେ ସୀମା ଦିଆଯାଇନାହିଁ",
    summaryOut: "{total}ଟି ମଧ୍ୟରୁ {n}ଟି ଫଳାଫଳ ରିପୋର୍ଟରେ ଦିଆଯାଇଥିବା ସୀମା ବାହାରେ ଅଛି।",
    summaryOk: "ସମସ୍ତ {total}ଟି ଫଳାଫଳ ରିପୋର୍ଟରେ ଦିଆଯାଇଥିବା ସୀମା ମଧ୍ୟରେ ଅଛି।",
    safety: "ସମଝୋ ପ୍ରତ୍ୟେକ ଫଳାଫଳକୁ କେବଳ ରିପୋର୍ଟରେ ଛପା ସୀମା ସହିତ ତୁଳନା କରେ। ଏହା ରୋଗ ନିର୍ଣ୍ଣୟ ନୁହେଁ। ରିପୋର୍ଟଟି ନିଶ୍ଚୟ ଆପଣଙ୍କ ଡାକ୍ତରଙ୍କୁ ଦେଖାନ୍ତୁ।",
    showDoctor: "ଏଗୁଡ଼ିକ ଡାକ୍ତରଙ୍କୁ ଦେଖାନ୍ତୁ",
    range: "ସୀମା",
    yourValue: "ଆପଣଙ୍କ ଫଳାଫଳ",
    tests: {
      haemoglobin: "ଅମ୍ଳଜାନ ବହନ କରୁଥିବା ରକ୍ତର ଅଂଶ",
      wbc: "ସଂକ୍ରମଣ ସହ ଲଢ଼ୁଥିବା ଧଳା ରକ୍ତ କଣିକା",
      platelets: "ରକ୍ତ ଜମାଟ ବାନ୍ଧିବାରେ ସାହାଯ୍ୟ କରୁଥିବା କୋଷ",
      rbc: "ଲାଲ ରକ୍ତ କଣିକା",
      fasting_sugar: "ରାତିସାରା ନ ଖାଇବା ପରେ ରକ୍ତରେ ଶର୍କରା",
      pp_sugar: "ଖାଇବାର ପ୍ରାୟ 2 ଘଣ୍ଟା ପରେ ରକ୍ତରେ ଶର୍କରା",
      random_sugar: "ଦିନର ଯେକୌଣସି ସମୟରେ ରକ୍ତରେ ଶର୍କରା",
      hba1c: "ଗତ 3 ମାସର ହାରାହାରି ଶର୍କରା",
      cholesterol: "ରକ୍ତରେ ମୋଟ କୋଲେଷ୍ଟେରଲ (ଏକ ପ୍ରକାର ଚର୍ବି)",
      ldl: "\"ଖରାପ\" କୋଲେଷ୍ଟେରଲ",
      hdl: "\"ଭଲ\" କୋଲେଷ୍ଟେରଲ",
      triglycerides: "ରକ୍ତରେ ଏକ ପ୍ରକାର ଚର୍ବି",
      creatinine: "କିଡ଼ନୀ କିପରି କାମ କରୁଛି ଦେଖାଉଥିବା ବର୍ଜ୍ୟ ପଦାର୍ଥ",
      urea: "କିଡ଼ନୀ ବାହାର କରୁଥିବା ବର୍ଜ୍ୟ ପଦାର୍ଥ",
      uric_acid: "ରକ୍ତରେ ଏକ ବର୍ଜ୍ୟ ପଦାର୍ଥ",
      sgpt: "ଯକୃତର ଏକ ଏନଜାଇମ୍ (ALT)",
      sgot: "ଯକୃତର ଏକ ଏନଜାଇମ୍ (AST)",
      bilirubin: "ଯକୃତ ସମ୍ଭାଳୁଥିବା ହଳଦିଆ ପଦାର୍ଥ",
      tsh: "ଥାଇରଏଡ୍ କିପରି କାମ କରୁଛି ଦେଖାଉଥିବା ହରମୋନ୍",
      vitamin_d: "ଭିଟାମିନ୍ D ସ୍ତର",
      vitamin_b12: "ଭିଟାମିନ୍ B12 ସ୍ତର",
      esr: "ଶରୀରରେ ପ୍ରଦାହର ଏକ ସାଧାରଣ ସଙ୍କେତ",
    }
  },
  as: {
    title: "পৰীক্ষাৰ ফলাফল",
    high: "ৰিপৰ্টত দিয়া সীমাতকৈ বেছি",
    low: "ৰিপৰ্টত দিয়া সীমাতকৈ কম",
    normal: "ৰিপৰ্টত দিয়া সীমাৰ ভিতৰত",
    noRange: "ৰিপৰ্টত সীমা দিয়া নাই",
    summaryOut: "{total}টাৰ ভিতৰত {n}টা ফলাফল ৰিপৰ্টত দিয়া সীমাৰ বাহিৰত।",
    summaryOk: "আটাইকেইটা {total}টা ফলাফল ৰিপৰ্টত দিয়া সীমাৰ ভিতৰত।",
    safety: "সমঝোৱে প্ৰতিটো ফলাফল কেৱল ৰিপৰ্টত ছপা সীমাৰ সৈতে তুলনা কৰে। এইটো ৰোগ নিৰ্ণয় নহয়। ৰিপৰ্টখন নিশ্চয় আপোনাৰ চিকিৎসকক দেখুৱাওক।",
    showDoctor: "এইবোৰ চিকিৎসকক দেখুৱাওক",
    range: "সীমা",
    yourValue: "আপোনাৰ ফলাফল",
    tests: {
      haemoglobin: "অক্সিজেন কঢ়িয়াই নিয়া তেজৰ অংশ",
      wbc: "সংক্ৰমণৰ বিৰুদ্ধে যুঁজা বগা তেজৰ কোষ",
      platelets: "তেজ গোট মৰাত সহায় কৰা কোষ",
      rbc: "ৰঙা তেজৰ কোষ",
      fasting_sugar: "গোটেই ৰাতি নাখাই থকাৰ পিছত তেজত চেনি",
      pp_sugar: "খোৱাৰ প্ৰায় ২ ঘণ্টা পিছত তেজত চেনি",
      random_sugar: "দিনৰ যিকোনো সময়ত তেজত চেনি",
      hba1c: "যোৱা ৩ মাহৰ গড় চেনি",
      cholesterol: "তেজত মুঠ কলেষ্টেৰল (এবিধ চৰ্বি)",
      ldl: "\"বেয়া\" কলেষ্টেৰল",
      hdl: "\"ভাল\" কলেষ্টেৰল",
      triglycerides: "তেজত এবিধ চৰ্বি",
      creatinine: "বৃক্কই কেনেকৈ কাম কৰিছে দেখুওৱা এবিধ আৱৰ্জনা",
      urea: "বৃক্কই উলিয়াই দিয়া এবিধ আৱৰ্জনা",
      uric_acid: "তেজত থকা এবিধ আৱৰ্জনা",
      sgpt: "যকৃতৰ এবিধ এনজাইম (ALT)",
      sgot: "যকৃতৰ এবিধ এনজাইম (AST)",
      bilirubin: "যকৃতে চম্ভালা এবিধ হালধীয়া পদাৰ্থ",
      tsh: "থাইৰইডে কেনেকৈ কাম কৰিছে দেখুওৱা এবিধ হৰমোন",
      vitamin_d: "ভিটামিন D ৰ মাত্ৰা",
      vitamin_b12: "ভিটামিন B12 ৰ মাত্ৰা",
      esr: "শৰীৰত প্ৰদাহৰ এটা সাধাৰণ চিন",
    }
  },
}
